import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/nav/PublicNav";
import Footer from "@/components/nav/Footer";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function Contact() {
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") || "";
  const source = params.get("source") || "contact_form";
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    message: initialPlan === "business" ? "We're interested in Hired's Business plan for our organization." : "",
    plan_slug: initialPlan,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, source });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <PublicNav />
      <section className="border-b border-line">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-28">
          <div className="brand-eyebrow mb-6">TALK TO HIRED</div>
          <h1 className="brand-headline text-6xl md:text-8xl">
            Tell us what<br /><span className="text-purple italic">you need.</span>
          </h1>
          <p className="text-steel text-lg md:text-xl mt-8 max-w-2xl">
            {initialPlan === "business"
              ? "Business plan is bespoke. Share a bit about your team and we'll set up a call."
              : "Demo request, sales question, partnership, or press — reach the team."}
          </p>

          {done ? (
            <div data-testid="contact-thanks" className="mt-14 card-white p-10 max-w-2xl">
              <CheckCircle2 className="text-purple" size={28} />
              <div className="brand-headline text-3xl mt-4 text-obsidian">Message received.</div>
              <p className="text-obsidian/70 mt-3">We&apos;ll get back to you within one business day.</p>
              <button onClick={() => nav("/")} className="mt-8 inline-flex text-obsidian border border-obsidian px-5 py-3 text-sm font-semibold hover:bg-obsidian hover:text-ivory transition-colors">
                Back to home
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="mt-14 border border-line p-8 md:p-10 max-w-2xl bg-graphite/40 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label htmlFor="c-name">Name</label>
                  <input data-testid="contact-name" id="c-name" required
                         value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                <div><label htmlFor="c-email">Email</label>
                  <input data-testid="contact-email" id="c-email" type="email" required
                         value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              </div>
              <div><label htmlFor="c-company">Company</label>
                <input data-testid="contact-company" id="c-company"
                       value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
              <div><label htmlFor="c-message">Message</label>
                <textarea data-testid="contact-message" id="c-message" rows="5" required
                          value={form.message} onChange={e => setForm({...form, message: e.target.value})} /></div>
              <button data-testid="contact-submit" type="submit" disabled={busy} className="btn-gold">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Send message <ArrowRight size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
